import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { RootState } from "@/shared/redux/store/store";
import { mergeStructuredOcrDocuments } from "@/shared/utils/documentOcr";
import { TRAVEL_QUOTE_STEP_KEYS, TravelQuoteStepKey } from "../../common/static";
import {
  SaveTravelQuoteStepPayload,
  TravelAdditionalInfoValues,
  TravelDetailsFormValues,
  TravelDocumentUploadFieldKey,
  TravelDocumentUploadValues,
  TravelPaymentValues,
  TravelPersonalFormValues,
  TravelQuoteFormState,
  TravelQuoteSelectionValues,
  TravelUploadedDocumentMap,
} from "../type";

export type TravelTravellerFormValue = {
  name?: string;
  dob: string;
};

export const defaultTravelPersonalFormValues: TravelPersonalFormValues = {
  firstName: "",
  lastName: "",
  mobileNumber: {
    countryCode: LOCALISATION_CONFIG.mobileCountryCode,
    countryCodeIso: LOCALISATION_CONFIG.mobileCountryIso,
    number: "",
  },
  email: "",
  partnerCode: "",
  friendsAndFamilyContact: "",
  marketingConsent: "no",
};

export const defaultTravelDetailsFormValues: TravelDetailsFormValues = {
  coverage: "",
  coverageLabel: "",
  fromCountry: "",
  destination: "",
  planType: "single",
  startDate: "",
  endDate: "",
  coverType: "individual",
  travellers: [{ name: "", dob: "" }],
};

export const defaultTravelQuoteSelectionValues: TravelQuoteSelectionValues = {
  selectedQuoteId: "",
};

export const defaultTravelDocumentUploadValues: TravelDocumentUploadValues = {
  passport: false,
  passportBack: false,
  passportCombined: false,
  visaCopy: false,
  confirmDocuments: false,
  emiratesIdFront: false,
  emiratesIdBack: false,
  emiratesIdCombined: false,
};

export const defaultTravelAdditionalInfoValues: TravelAdditionalInfoValues = {
  gender: "",
  nationality: "",
  cityOfResidence: "",
  address: "",
  emiratesId: "",
  passportNumber: "",
  sourceOfFund: "",
  beneficiaryName: "",
  beneficiaryRelation: "",
  uaeResidentConfirm: false,
  travellers: [],
};

export const defaultTravelPaymentValues: TravelPaymentValues = {
  paymentMethod: "Card",
  billingEmail: "",
  cardHolder: "",
  cardNumber: "",
  expiry: "",
  cvv: "",
  confirmPayment: false,
};

export const defaultTravelQuoteFormState: TravelQuoteFormState = {
  request: null,
  documentUploadDocuments: {},
  [TRAVEL_QUOTE_STEP_KEYS.PERSONAL_DETAILS]: defaultTravelPersonalFormValues,
  [TRAVEL_QUOTE_STEP_KEYS.TRAVEL_DETAILS]: defaultTravelDetailsFormValues,
  [TRAVEL_QUOTE_STEP_KEYS.QUOTE_SELECTION]: defaultTravelQuoteSelectionValues,
  [TRAVEL_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD]: defaultTravelDocumentUploadValues,
  [TRAVEL_QUOTE_STEP_KEYS.ADDITIONAL_INFO]: defaultTravelAdditionalInfoValues,
  [TRAVEL_QUOTE_STEP_KEYS.PAYMENT]: defaultTravelPaymentValues,
};

function createSaveTravelQuoteStepPayload<TStepKey extends TravelQuoteStepKey>(
  stepName: TStepKey,
  data: TravelQuoteFormState[TStepKey]
): Extract<SaveTravelQuoteStepPayload, { stepName: TStepKey }> {
  return { stepName, data } as Extract<SaveTravelQuoteStepPayload, { stepName: TStepKey }>;
}

export function saveTravelQuoteStepFor<TStepKey extends TravelQuoteStepKey>(
  stepName: TStepKey,
  data: TravelQuoteFormState[TStepKey]
) {
  return saveTravelQuoteStep(createSaveTravelQuoteStepPayload(stepName, data));
}

const travelQuoteSlice = createSlice({
  name: "travelQuote",
  initialState: defaultTravelQuoteFormState,
  reducers: {
    saveTravelQuoteStep(state, action: PayloadAction<SaveTravelQuoteStepPayload>) {
      switch (action.payload.stepName) {
        case TRAVEL_QUOTE_STEP_KEYS.PERSONAL_DETAILS:
          state[TRAVEL_QUOTE_STEP_KEYS.PERSONAL_DETAILS] = action.payload.data;
          break;
        case TRAVEL_QUOTE_STEP_KEYS.TRAVEL_DETAILS:
          state[TRAVEL_QUOTE_STEP_KEYS.TRAVEL_DETAILS] = action.payload.data;
          break;
        case TRAVEL_QUOTE_STEP_KEYS.QUOTE_SELECTION:
          state[TRAVEL_QUOTE_STEP_KEYS.QUOTE_SELECTION] = action.payload.data;
          break;
        case TRAVEL_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD:
          state[TRAVEL_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD] = action.payload.data;
          break;
        case TRAVEL_QUOTE_STEP_KEYS.ADDITIONAL_INFO:
          state[TRAVEL_QUOTE_STEP_KEYS.ADDITIONAL_INFO] = action.payload.data;
          break;
        case TRAVEL_QUOTE_STEP_KEYS.PAYMENT:
          state[TRAVEL_QUOTE_STEP_KEYS.PAYMENT] = action.payload.data;
          break;
      }
    },
    resetTravelQuoteState() {
      return defaultTravelQuoteFormState;
    },
    saveTravelUploadedDocument(
      state,
      action: PayloadAction<{
        fieldKey: TravelDocumentUploadFieldKey;
        document: TravelUploadedDocumentMap[TravelDocumentUploadFieldKey];
      }>
    ) {
      const { fieldKey, document } = action.payload;

      if (!document) {
        delete state.documentUploadDocuments[fieldKey];
        return;
      }

      state.documentUploadDocuments[fieldKey] = document;
    },
  },
});

export const { resetTravelQuoteState, saveTravelQuoteStep, saveTravelUploadedDocument } =
  travelQuoteSlice.actions;

export const selectTravelQuoteState = (state: RootState) => state.travelQuote;
export const selectTravelUploadedDocuments = (state: RootState) =>
  state.travelQuote.documentUploadDocuments;
export const selectTravelStructuredOcrData = (state: RootState) =>
  mergeStructuredOcrDocuments(state.travelQuote.documentUploadDocuments);

export default travelQuoteSlice.reducer;
